import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Button, Modal, Form, Input, Select, Space, message, Checkbox, DatePicker, Calendar, Badge, Row, Col, Segmented, Switch } from "antd";
import moment from "moment";
import { PlusOutlined, BellOutlined } from "@ant-design/icons";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { Option } = Select;

const bookingStatuses = ["Reserved", "Confirmed", "Completed", "Cancelled"];

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [reminderForm] = Form.useForm();
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("bookings.viewMode");
    return saved === "table" || saved === "cards" || saved === "calendar" ? saved : "cards";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: [] });
  const [isDark, setIsDark] = useState(true);
  const useDarkStyles = true;

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(API_CONFIG.bookingsListUrl, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load bookings");
      setBookings(data.bookings || []);
    } catch (err) {
      message.error(err.message || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    localStorage.setItem("bookings.viewMode", viewMode);
  }, [viewMode]);

  const handleSave = async (values) => {
    const payload = { ...values, date: values.date?.toISOString?.() || values.date };
    await apiFetch(API_CONFIG.bookingsSaveUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    setOpen(false);
    fetchBookings();
  };

  const updateStatus = async (booking, status) => {
    await apiFetch(API_CONFIG.bookingsStatusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: booking.id, status }),
    });
    fetchBookings();
  };

  const updateBookingDate = async (booking, date) => {
    await apiFetch(API_CONFIG.bookingsSaveUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: booking.id, date }),
    });
    fetchBookings();
  };

  const openComment = (booking) => {
    setSelectedBooking(booking);
    commentForm.resetFields();
    setCommentOpen(true);
  };

  const submitComment = async (values) => {
    await apiFetch(API_CONFIG.bookingsCommentUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: selectedBooking.id, text: values.comment }),
    });
    setCommentOpen(false);
    fetchBookings();
  };

  const openReminder = (booking) => {
    setSelectedBooking(booking);
    reminderForm.resetFields();
    setReminderOpen(true);
  };

  const submitReminder = async (values) => {
    const dueDate = values.dueDate?.toISOString?.() || values.dueDate;
    await apiFetch(API_CONFIG.notificationsSaveUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "booking",
        message: `Reminder: ${selectedBooking.clientName} booking`,
        dueDate,
      }),
    });
    setReminderOpen(false);
    message.success("Reminder scheduled");
  };

  const columns = [
    { title: "Booking ID", dataIndex: "id", key: "id", width: 120 },
    { title: "Client", dataIndex: "clientName", key: "clientName" },
    { title: "Service", dataIndex: "service", key: "service" },
    { title: "Date", dataIndex: "date", key: "date" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, booking) => (
        <Select value={booking.status} onChange={(v) => updateStatus(booking, v)} size="small">
          {bookingStatuses.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Terms",
      dataIndex: "termsAccepted",
      key: "termsAccepted",
      render: (v) => (v ? "Accepted" : "Pending"),
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, booking) => (
        <Space>
          <Button size="small" onClick={() => openComment(booking)}>Comment</Button>
          <Button size="small" icon={<BellOutlined />} onClick={() => openReminder(booking)}>Reminder</Button>
        </Space>
      ),
    },
  ];

  const toggleFilter = (value) => {
    setFilters((prev) => {
      const next = new Set(prev.status || []);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, status: Array.from(next) };
    });
  };
  const clearFilters = () => setFilters({ status: [] });

  const filteredBookings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return bookings.filter((b) => {
      const matchesSearch = !term || [b.clientName, b.service, b.id].filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
      const matchesStatus = filters.status.length === 0 || filters.status.includes(b.status);
      return matchesSearch && matchesStatus;
    });
  }, [bookings, searchTerm, filters]);

  const bookingsByDate = filteredBookings.reduce((acc, b) => {
    const key = moment(b.date).format("YYYY-MM-DD");
    acc[key] = acc[key] ? [...acc[key], b] : [b];
    return acc;
  }, {});

  const statusColor = (status) => {
    switch (status) {
      case "Confirmed":
        return "success";
      case "Reserved":
        return "warning";
      case "Cancelled":
        return "error";
      default:
        return "processing";
    }
  };

  const dateCellRender = (value) => {
    const key = value.format("YYYY-MM-DD");
    const items = bookingsByDate[key] || [];
    const handleDrop = async (e) => {
      e.preventDefault();
      const bookingId = e.dataTransfer.getData("text/booking-id");
      if (!bookingId) return;
      const booking = bookings.find((b) => b.id === bookingId);
      if (!booking) return;
      await updateBookingDate(booking, value.toISOString());
      message.success(`Rescheduled ${booking.clientName}`);
    };
    const handleDragOver = (e) => e.preventDefault();
    return (
      <div onDrop={handleDrop} onDragOver={handleDragOver} style={{ minHeight: 24 }}>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {items.slice(0, 2).map((item) => (
            <li key={item.id}>
              <Badge status={statusColor(item.status)} text={item.clientName} />
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const [selectedDate, setSelectedDate] = useState(moment());
  const selectedKey = selectedDate.format("YYYY-MM-DD");
  const selectedBookings = bookingsByDate[selectedKey] || [];

  return (
    <div
      className={`appverse-content table-container tabled ${useDarkStyles ? "luxe-dark" : "luxe-light"}`}
      style={{
        background: isDark ? "#000" : "#fff",
        minHeight: "100vh",
        marginLeft: "var(--sidenav-width, 88px)",
        padding: "0 20px 0 12px",
        position: "relative",
        zIndex: 1002,
      }}
    >
      <Row gutter={0}>
        <Col xs={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24 luxe-dark-bg"
            style={{ margin: 0, position: "relative", zIndex: 1003 }}
            title={<span className="luxe-card-title">Bookings</span>}
            extra={
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="mode-toggle">
                  <Segmented
                    options={[
                      { label: "Cards", value: "cards" },
                      { label: "Table", value: "table" },
                      { label: "Calendar", value: "calendar" },
                    ]}
                    value={viewMode}
                    onChange={(v) => setViewMode(v)}
                  />
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 4 }}>
                  <span style={{ color: "#fff8dc", fontWeight: 600 }}>Dark</span>
                  <Switch checked={isDark} onChange={(v) => setIsDark(v)} />
                </div>
              </div>
            }
            bodyStyle={{ background: "#000", borderRadius: "18px" }}
          >
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <Button className="luxe-btn" type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
                New Booking
              </Button>
            </div>

            <Input.Search
              placeholder="Search bookings by client, service, or ID"
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              className="luxe-search"
              enterButton={<Button className="luxe-search-btn">Search</Button>}
            />

            <div className="filter-chips">
              <span className="chip-filter">Status</span>
              {bookingStatuses.map((status) => (
                <span
                  key={status}
                  className={`chip-filter ${filters.status.includes(status) ? "active" : ""}`}
                  onClick={() => toggleFilter(status)}
                >
                  {status}
                </span>
              ))}
              {filters.status.length > 0 && (
                <span className="chip-filter clear" onClick={clearFilters}>Clear</span>
              )}
            </div>

            {viewMode === "cards" && (
              <div className="indigent-grid" style={{ marginTop: 20 }}>
                {filteredBookings.map((b) => (
                  <div key={b.id} className="indigent-card">
                    <div className="indigent-head">
                      <div className="indigent-avatar">{(b.clientName || "B")[0]}</div>
                      <div>
                        <div className="indigent-name">{b.clientName}</div>
                        <div className="indigent-meta">{b.id} · {moment(b.date).format("YYYY-MM-DD")}</div>
                      </div>
                    </div>
                    <div className="indigent-tags">
                      <span className="chip dark">Service: {b.service}</span>
                      <span className="chip dark">Status: {b.status}</span>
                      <span className="chip dark">Terms: {b.termsAccepted ? "Accepted" : "Pending"}</span>
                    </div>
                    <div className="indigent-actions">
                      <Button className="luxe-btn" onClick={() => openComment(b)}>Comment</Button>
                      <Button className="luxe-btn" icon={<BellOutlined />} onClick={() => openReminder(b)}>Reminder</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === "table" && (
              <Table rowKey="id" loading={loading} dataSource={filteredBookings} columns={columns} className="luxe-table" style={{ marginTop: 16 }} />
            )}

            {viewMode === "calendar" && (
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={16}>
                  <Card title="Booking Calendar" bordered={false} className="luxe-dark-bg">
                    <div style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                      <span><Badge status="processing" /> Scheduled</span>
                      <span><Badge status="success" /> Confirmed</span>
                      <span><Badge status="warning" /> Reserved</span>
                      <span><Badge status="error" /> Cancelled</span>
                    </div>
                    <Calendar
                      fullscreen={false}
                      dateCellRender={dateCellRender}
                      value={selectedDate}
                      onSelect={(val) => setSelectedDate(val)}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  <Card title={`Bookings on ${selectedKey}`} bordered={false} className="luxe-dark-bg">
                    {selectedBookings.length ? (
                      selectedBookings.map((b) => (
                        <div
                          key={b.id}
                          style={{ marginBottom: 12, cursor: "grab" }}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData("text/booking-id", b.id)}
                        >
                          <strong>{b.clientName}</strong>
                          <div>{b.service}</div>
                          <div>Status: {b.status}</div>
                          <div style={{ fontSize: 12, opacity: 0.7 }}>Drag to calendar to reschedule</div>
                        </div>
                      ))
                    ) : (
                      <div>No bookings on this date.</div>
                    )}
                  </Card>
                  <Card title="Auto-Reminders" bordered={false} className="luxe-dark-bg" style={{ marginTop: 16 }}>
                    3-day reminders are automatically scheduled for upcoming bookings.
                  </Card>
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="New Booking" open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} okText="Save">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="clientName" label="Client Name" rules={[{ required: true, message: "Enter client name" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="service" label="Service" rules={[{ required: true, message: "Enter service" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="date" label="Booking Date" rules={[{ required: true, message: "Select date" }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="termsAccepted" valuePropName="checked">
            <Checkbox>Terms and Conditions accepted</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add Comment" open={commentOpen} onCancel={() => setCommentOpen(false)} onOk={() => commentForm.submit()} okText="Save">
        <Form form={commentForm} layout="vertical" onFinish={submitComment}>
          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: "Enter a comment" }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Schedule Reminder" open={reminderOpen} onCancel={() => setReminderOpen(false)} onOk={() => reminderForm.submit()} okText="Schedule">
        <Form form={reminderForm} layout="vertical" onFinish={submitReminder}>
          <Form.Item name="dueDate" label="Reminder Date" rules={[{ required: true, message: "Select reminder date" }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
